import {
    MIGRATE_EXISTING_OPTION,
    ONBOARDING_CATEGORY,
    COMMON,
    TEAM_SUFFIX_DEVELOPERS,
    TEAM_TYPE,
    PIPELINE_SETUP_TYPE,
} from 'pages/provisioning/day2/github-onboarding-new/utils/constant';
import { getCustomTeamNameParts } from 'pages/provisioning/day2/github-onboarding-new/utils';

// Builds the team validation cache object keyed by team name
export const buildValidationCacheData = (teamValidations) =>
    teamValidations.reduce(
        (cache, { team, validation }) => ({
            ...cache,
            [team.teamName]: {
                teamExists: validation.teamExists,
                members: validation.apiMembers,
            },
        }),
        {}
    );

// Transforms team validation API response.
// For custom teams, extracts name parts (capability, appName, etc.) from the team name.
export const parseTeamsFromValidations = (teamValidations, businessApplication) =>
    teamValidations.map(({ team, validation }) => {
        const parsedMembers = (team?.members || []).map((m) => ({
            name: m?.name,
            email: m?.emailId,
            login: m?.gpId,
            gpid: m?.gpId,
            hasCopilotLicense: m?.coPilotLicense ?? null,
            isPartOfBaseAdGroups: m?.isPartOfBaseAdGroups,
        }));

        const baseTeam = {
            name: team?.teamName,
            isDefault: team?.type === TEAM_TYPE.DEFAULT,
            isNew: team?.type === TEAM_TYPE.CUSTOM,
            repos: team?.repos || [],
            originalRepos: team?.repos || [],
            members: parsedMembers,
            originalMembers: parsedMembers,
            existingOrgMembers: validation?.existingOrgMembers,
            removedMembers: Array.isArray(team?.removedMembers)
                ? team.removedMembers.map((rm) => ({
                      name: rm?.name || '',
                      gpId: rm?.gpId || rm?.gpid || '',
                      emailId: rm?.emailId || rm?.email || '',
                      hasCopilotLicense: rm?.hasCopilotLicense ?? rm?.coPilotLicense ?? null,
                      isPartOfBaseAdGroups: rm?.isPartOfBaseAdGroups,
                  }))
                : [],
            hasExistingMembers: validation?.hasExistingMembers,
            teamExistsInOrg: validation?.teamExists,
        };

        if (team?.type === TEAM_TYPE.CUSTOM) {
            const nameParts = getCustomTeamNameParts(team?.teamName, businessApplication);
            if (nameParts?.capability) {
                return {
                    ...baseTeam,
                    capability: nameParts?.capability,
                    appName: nameParts?.appName,
                    customName: nameParts?.customName || '',
                    categorization: nameParts?.categorization || TEAM_SUFFIX_DEVELOPERS,
                };
            }
        }

        return baseTeam;
    });

// Maps saved repo migration data based on migration platform (ADO or GitHub)
export const parseReposToMigrate = (catalogConfig) => {
    if (catalogConfig?.onboardingType !== ONBOARDING_CATEGORY.MIGRATE) return [];

    if (catalogConfig?.platformType === MIGRATE_EXISTING_OPTION.GITHUB) {
        return (catalogConfig?.repositoryToMigrate || []).map((repo) => ({
            githubName: repo?.sourceRepositoryName,
            githubEnterpriseName: repo?.targetRepositoryName,
        }));
    }

    if (catalogConfig?.platformType === MIGRATE_EXISTING_OPTION.ADO) {
        return (catalogConfig?.repositoryToMigrate || []).map((repo) => ({
            adoName: repo?.sourceRepositoryName,
            githubName: repo?.targetRepositoryName,
        }));
    }

    return [];
};

// Maps saved ArgoCD repo migration data back into the section's local shape
export const parseArgoCDReposToMigrate = (catalogConfig) => {
    if (catalogConfig?.onboardingType !== ONBOARDING_CATEGORY.MIGRATE) return [];

    return (catalogConfig?.argocdRepositoryToMigrate || []).map((repo) => ({
        adoArgoCDRepoName: repo?.sourceRepositoryName,
        editableName: repo?.sourceRepositoryName,
        newGithubArgoCDRepoName: repo?.targetRepositoryName,
    }));
};

// Builds the SonarQube config object from saved order data; returns null if SonarQube is not configured
export const buildSonarQubeConfig = (catalogConfig) => {
    const hasSonarQubeIntegration = catalogConfig?.sonarQubeIntegration;
    if (hasSonarQubeIntegration === undefined || hasSonarQubeIntegration === null) return null;

    const sonarQubeData = catalogConfig?.sonarQube;
    let repositoriesWithSonar = [];
    let validationResults = [];
    let modifiedProjects = [];

    if (catalogConfig?.onboardingType === ONBOARDING_CATEGORY.MIGRATE) {
        // Include all repos that have sonarQubeOnboarded defined (true or false)
        const allReposWithSonarInfo = (catalogConfig?.repositoryToMigrate || []).filter((repo) => typeof repo?.sonarQubeOnboarded !== 'undefined');

        // repositoriesWithSonar should remain only those that are onboarded and have a project key
        repositoriesWithSonar = allReposWithSonarInfo.filter((repo) => repo?.sonarQubeOnboarded === true && repo?.sonarQubeProjectKey);

        // validationResults must include both onboarded and non-onboarded repos so edit mode shows both
        validationResults = allReposWithSonarInfo.map((repo) => ({
            repositoryName: repo.sourceRepositoryName,
            availableInSonar: repo.sonarQubeOnboarded === true,
            ...(repo.sonarQubeProjectKey ? { sonarQubeProjectKey: repo.sonarQubeProjectKey } : {}),
            ...(repo.updatedSonarQubeProjectKey ? { updatedSonarQubeProjectKey: repo.updatedSonarQubeProjectKey } : {}),
        }));

        // Repos whose SonarQube project key was renamed compared to the original (only consider onboarded ones)
        modifiedProjects = repositoriesWithSonar
            .filter((repo) => repo.updatedSonarQubeProjectKey && repo.updatedSonarQubeProjectKey !== repo.sonarQubeProjectKey)
            .map((repo) => ({
                existingProjectName: repo.sonarQubeProjectKey,
                newProjectName: repo.updatedSonarQubeProjectKey,
            }));
    } else if (catalogConfig?.onboardingType === ONBOARDING_CATEGORY.NEW) {
        validationResults = (catalogConfig?.githubRepository || [])
            .filter((repo) => repo?.sonarQubeOnboarded !== undefined)
            .map((repo) => ({
                repositoryName: repo.githubRepositoryName,
                availableInSonar: repo.sonarQubeOnboarded === true,
            }));
    }

    const hasModifiedProjects = modifiedProjects.length > 0;
    const normalizedLegacyServiceAccount = typeof sonarQubeData?.serviceAccount === 'string' ? sonarQubeData.serviceAccount.trim() : '';
    const normalizedGroupServiceAccount = typeof sonarQubeData?.sonarGroupServiceAccount === 'string' ? sonarQubeData.sonarGroupServiceAccount.trim() : '';
    const normalizedServiceAccountValue = normalizedLegacyServiceAccount || null;
    const normalizedGroupServiceAccountValue = normalizedGroupServiceAccount || null;
    const normalizedServiceAccountExists = typeof sonarQubeData?.isServiceAccountExists === 'string' ? sonarQubeData.isServiceAccountExists.toLowerCase() : '';
    const isFromSonarGroup = !!normalizedGroupServiceAccountValue;
    const hasValidServiceAccountFromCatalog = isFromSonarGroup
        ? !!normalizedGroupServiceAccountValue
        : normalizedServiceAccountExists === 'yes' && !!normalizedServiceAccountValue;
    let serviceAccountExistsValue = null;
    if (normalizedServiceAccountExists === 'yes') {
        serviceAccountExistsValue = COMMON.YES;
    } else if (normalizedServiceAccountExists === 'no') {
        serviceAccountExistsValue = COMMON.NO;
    }

    return {
        requiresIntegration: hasSonarQubeIntegration ? COMMON.YES : COMMON.NO,
        validationData: validationResults.length > 0 ? { results: validationResults } : null,
        shouldValidate: false,
        isSonarGroupServiceAccount: isFromSonarGroup,
        serviceAccountsFromApi: isFromSonarGroup && normalizedGroupServiceAccountValue ? [normalizedGroupServiceAccountValue] : [],
        sonarGroupServiceAccount: isFromSonarGroup ? normalizedGroupServiceAccountValue : null,
        sonarGroupServiceAccountValid: hasValidServiceAccountFromCatalog,
        isServiceAccountExists: serviceAccountExistsValue,
        ...(!isFromSonarGroup &&
            sonarQubeData?.isServiceAccountExists && {
                hasServiceAccount: normalizedServiceAccountExists === 'yes' ? COMMON.YES : COMMON.NO,
                ...(normalizedServiceAccountExists === 'yes' &&
                    normalizedServiceAccountValue && {
                        serviceAccount: normalizedServiceAccountValue,
                    }),
            }),
        ...(catalogConfig?.onboardingType === ONBOARDING_CATEGORY.MIGRATE && {
            ...(hasModifiedProjects && {
                shouldModifyProjectName: COMMON.YES,
                modifiedSonarProjects: modifiedProjects,
            }),
            ...(repositoriesWithSonar.length > 0 &&
                !hasModifiedProjects && {
                    shouldModifyProjectName: COMMON.NO,
                }),
        }),
    };
};

// Builds initial JFrog repo config entries from ADO migration repos
export const buildMigrateJfrogRepoConfigs = (catalogConfig) =>
    (catalogConfig?.repositoryToMigrate || [])
        .filter((repo) => repo?.targetRepositoryName && repo?.jfrogArtifactoryName)
        .map((repo) => ({
            repositoryName: repo.targetRepositoryName,
            jfrogRepoName: repo.jfrogArtifactoryName,
            jfrogGlobalRepo: null,
            packageManager: null,
            isNoneBuildTool: false,
            resolveError: null,
            resolvedMessage: null,
            isValidated: true,
            resolveAttempted: true,
        }));

export const buildNewOnboardingPipelineConfig = (catalogConfig) => {
    if (catalogConfig?.onboardingType !== ONBOARDING_CATEGORY.NEW) return [];

    return (catalogConfig?.githubRepository || []).map((repo) => {
        const repoName = repo?.githubRepositoryName || null;
        const pi = repo?.pipelineIntegration;
        if (!pi || repo?.language === COMMON.NONE) {
            return {
                repoName,
                pipelineSetupType: null,
                branchingStrategy: null,
                deploymentMechanism: null,
                deploymentPattern: null,
                globalTemplate: '',
                cataloguePath: '',
                buildPattern: '',
            };
        }
        let pipelineSetupType;
        if (pi?.pipelineSetup === PIPELINE_SETUP_TYPE.BUILD_AND_DEPLOYMENT) {
            pipelineSetupType = PIPELINE_SETUP_TYPE.BUILD_AND_DEPLOYMENT;
        } else if (pi?.pipelineSetup === PIPELINE_SETUP_TYPE.BUILD) {
            pipelineSetupType = PIPELINE_SETUP_TYPE.BUILD;
        } else if (pi?.deployMechanism || pi?.branchingStrategy) {
            pipelineSetupType = PIPELINE_SETUP_TYPE.BUILD_AND_DEPLOYMENT;
        } else {
            pipelineSetupType = PIPELINE_SETUP_TYPE.BUILD;
        }

        return {
            repoName,
            pipelineSetupType,
            branchingStrategy: pi?.branchingStrategy || null,
            deploymentMechanism: pi?.deployMechanism || null,
            deploymentPattern: pi?.deploymentPattern || null,
            globalTemplate: pi?.globalTemplate || '',
            cataloguePath: pi?.githubCataloguePath || '',
            buildPattern: pi?.buildPattern || '',
        };
    });
};

// Builds JFrog repo config entries for new GitHub repo creation
export const buildNewRepoJfrogConfigs = (catalogConfig) =>
    (catalogConfig?.githubRepository || [])
        .filter((repo) => {
            if (!repo?.githubRepositoryName) return false;
            const isNoneBuildTool = !repo?.buildTool && repo?.language === COMMON.NONE;
            return Boolean(repo?.jfrogArtifactoryName) || isNoneBuildTool;
        })
        .map((repo) => {
            const isNoneBuildTool = !repo?.buildTool && repo?.language === COMMON.NONE;
            return {
                repositoryName: repo.githubRepositoryName,
                jfrogRepoName: repo.jfrogArtifactoryName,
                jfrogGlobalRepo: null,
                packageManager: repo?.buildTool || null,
                isNoneBuildTool,
                resolveError: null,
                resolvedMessage: null,
                isValidated: true,
            };
        });
